import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import styles from './File.module.scss';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';

import {
    downloadDarkGray,
    downloadIconDataRoom,
    downloadIconDataRoomHovered,
    fileIconDataRoom,
    PURPLE_THREE_DOTS_VERTICAL,
    infoIconGreen,
    infoIconRed,
} from '../../../../../../assets/hostedassets';
import infoIconYellow from '../../../../../../assets/info-yellow.svg';
import infoIconBlue from '../../../../../../assets/info-blue.svg';
import threeDotVertical from '../../../../../../assets/three-dot-vertical.svg';
import useFetchDataRoomFiles from '../../../../useFetchDataRoomFiles';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';
import DocxDrawer from '../../../../../../common/DocumentViewer/DocxDrawer';
import { SheetEditorDrawer, isEditableSheetFile } from '../../../../../../common/SheetEditor';
import { classifyAddable } from '../../../../../../common/Reports/addFileToReports';
import type { AddToWorkspacePayload } from '../../../../../../common/Reports/addFileToReports';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../../app/hooks';
import {
    AICA_DATAROOM_FILE_ARCHIVE,
    AICA_DATAROOM_FILE_DELETE,
    AICA_DATAROOM_FILE_DOWNLOAD,
    AICA_DATAROOM_FILE_MOVE,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { FILE_FOLDER_TYPES } from '../../../../../../store/investor/constants';
import { isLenderType } from '../../../../../../utils/utils';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import type { FeatureVisibility } from '../../../../../../utils/visibilityConfigUtils';
import { isSignableFileName } from '../../../../../../common/MagicSign/utils';
import { computeDropdownTop, DROPDOWN_EDGE_PADDING } from '../dropdownPositionUtils';
import { CHIP_TOOLTIP_TEXT, INGESTION_ACTION, INGESTION_CHIP, MAX_STATUS_REASONS, REUPLOAD_MENU_LABEL } from '../../../../constants';
import type { BankStatementFileMetaData } from '../../../../types';

// Per-status pill tone, matching the Figma ingestion-status frames.
const CHIP_TONE_CLASS: Partial<Record<string, string>> = {
    [INGESTION_CHIP.PENDING]: styles.ChipPending,
    [INGESTION_CHIP.DATA_EXTRACTED]: styles.ChipDataExtracted,
    [INGESTION_CHIP.PARTIALLY_EXTRACTED]: styles.ChipPending,
    [INGESTION_CHIP.FAILED]: styles.ChipFailed,
    [INGESTION_CHIP.UNSUPPORTED_FILE]: styles.ChipFailed,
    [INGESTION_CHIP.IN_PROGRESS]: styles.ChipInProgress,
};

const CHIP_INFO_ICON: Partial<Record<string, string>> = {
    [INGESTION_CHIP.PENDING]: infoIconYellow,
    [INGESTION_CHIP.DATA_EXTRACTED]: infoIconGreen,
    [INGESTION_CHIP.PARTIALLY_EXTRACTED]: infoIconYellow,
    [INGESTION_CHIP.FAILED]: infoIconRed,
    [INGESTION_CHIP.UNSUPPORTED_FILE]: infoIconRed,
    [INGESTION_CHIP.IN_PROGRESS]: infoIconBlue,
};

// Chips whose tooltip explains the doc's own reason (bank and provisional) instead of generic copy.
const REASON_TOOLTIP_CHIPS: ReadonlySet<string> = new Set([
    INGESTION_CHIP.FAILED,
    INGESTION_CHIP.UNSUPPORTED_FILE,
    INGESTION_CHIP.PARTIALLY_EXTRACTED,
]);

const File = ({
    name,
    lastUpdated,
    index,
    docId,
    masterFolderName,
    fetchFiles,
    setShowDataRoomModal,
    isNew,
    startDate,
    endDate,
    selectedItems = new Set(),
    onItemSelect,
    showCheckboxes = false,
    dataRoomDownloadVisibility,
    addToReportsVisible,
    onSendForSigning,
    signableExtensions = [],
    signedDocId,
    hasSignedCopy,
    showSignedCopyVisibility,
    fileMetaData,
    onReupload,
}: {
    name: string;
    lastUpdated: string;
    index: number;
    docId?: any;
    masterFolderName: string;
    fetchFiles: any;
    setShowDataRoomModal: any;
    isNew?: boolean;
    startDate?: string;
    endDate?: string;
    selectedItems?: Set<string>;
    onItemSelect?: (itemId: string) => void;
    showCheckboxes?: boolean;
    dataRoomDownloadVisibility: FeatureVisibility;
    addToReportsVisible: boolean;
    onSendForSigning?: (docId: string, fileName: string) => void;
    /**
     * File extensions that are eligible for signing (e.g. ['.pdf']).
     * Passed from FileExplorer (which owns the MagicSign visibility check)
     * so that File.tsx does not need to import from common/MagicSign directly.
     */
    signableExtensions?: string[];
    /** Mnemosyne doc id of the latest signed copy. Present only when the backend has populated it. */
    signedDocId?: string | null;
    /** True when a signed copy exists for this file. */
    hasSignedCopy?: boolean;
    /**
     * Visibility flag for the signed-copy toggle. Passed once from FileExplorer
     * (the stable container) — never fetched per-row.
     */
    showSignedCopyVisibility?: FeatureVisibility;
    /** Bank-statement ingestion signal, projected onto this file's metaData. */
    fileMetaData?: BankStatementFileMetaData;
    /** Opens the Data Room upload drawer; passed once from FileExplorer (never built per row). */
    onReupload?: () => void;
}) => {
    const [downloadImgUrl, setDownloadImgUrl] = useState(downloadIconDataRoomHovered);
    const [showSignedVersion, setShowSignedVersion] = useState(false);
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

    const dropdownRef: any = useRef(null);
    const threeDotsRef: any = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const fileViewType = searchParams.get('fileViewType');
    const isLender = isLenderType();
    const isArchiveView = fileViewType === 'archived';
    // MCA records are read-only sweep documents: no archiving from the UI, so they never leave the tab.
    const isMcaRecordsView = fileViewType === 'mcaRecords';

    const {
        handleFileClick,
        showFile,
        setShowFile,
        showSheetEditor,
        setShowSheetEditor,
        showDocxDrawer,
        setShowDocxDrawer,
        handleEditSheet,
        fileUrl,
        fileMimeType,
        downloadTheFile,
        handleDeleteFile,
        handleUnarchiveFileBulk,
        getFormattedDate,
        downloadingFile,
        MoveToActive,
        DeleteButton,
        MoveButton,
        ArchiveButton,
        AddToReportsButton,
        handleAddToReports,
        addingToReports,
        addedToReports,
    } = useFetchDataRoomFiles(fetchFiles);

    const canEditSheet = isEditableSheetFile(name);
    // S3-backed docIds (non-numeric) can't be snapshotted via handleAddToReports's
    // presigned-URL lookup — hide the affordance rather than showing an action
    // that errors on click.
    const canAddToReports = addToReportsVisible && classifyAddable(name) !== null && !isNaN(Number(docId));
    // Primary header action on the Sheet/Docx drawers — same gate + org context
    // as the three-dots "Add to Reports" entry point (both coexist), plus the
    // archive-view gate (three-dots already hides it there via canAddToReports
    // being checked inside the non-archive dropdown branch only).
    // The drawers hand back the EDITED on-screen content (or nothing when
    // untouched); the sheet drawer also the bytes it already holds for a
    // read-only-band workbook.
    const onDrawerAddToReports =
        docId && canAddToReports && !isArchiveView
            ? (payload?: AddToWorkspacePayload) => handleAddToReports(docId, name, payload)
            : undefined;

    const user = useAppSelector((state) => state.user.userData);
    const selectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const isPartner = user?.accountType === USER_TYPE.PARTNER;
    const isLenderOrPartner = isLender || isPartner;

    const fileId = docId ? String(docId) : name;
    const isSelected = selectedItems.has(fileId);

    // Compute the active doc id: signed copy when toggle is on, original otherwise.
    const showToggle = !!(signedDocId && hasSignedCopy && showSignedCopyVisibility?.show);
    const activeDocId = showToggle && showSignedVersion ? signedDocId : docId;

    const calculateDropdownPosition = () => {
        if (!threeDotsRef.current) return;
        //@ts-ignore
        const rect = threeDotsRef.current.getBoundingClientRect();
        const dropdownHeight = dropdownRef.current?.getBoundingClientRect().height ?? 0;
        setDropdownPosition({
            top: computeDropdownTop(rect, dropdownHeight),
            // Right-anchor the menu to the three-dots button so it grows leftward and
            // never clips the (wider) "Add to Reports" label off the right viewport edge.
            right: Math.max(window.innerWidth - rect.right, DROPDOWN_EDGE_PADDING),
        });
    };

    // useLayoutEffect (not useEffect): recalculates before the browser paints, so the
    // menu never flashes at its stale/initial position before snapping to the correct one.
    useLayoutEffect(() => {
        if (showOtherOptions) {
            calculateDropdownPosition();
        }
    }, [showOtherOptions]);

    useEffect(() => {
        const handleScroll = () => {
            if (showOtherOptions) {
                setShowOtherOptions(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showOtherOptions]);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                threeDotsRef.current &&
                !threeDotsRef.current?.contains(event.target)
            ) {
                setShowOtherOptions(false);
            }
        };
        if (showOtherOptions) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOtherOptions]);

    // Shared across both the active and archive dropdown branches so the "Add to Magic Sign"
    // entry point is available regardless of file view.
    const magicSignItem = isSignableFileName(name, signableExtensions) && onSendForSigning && (
        <div
            className={styles.DropdownItem}
            onClick={(e) => {
                e.stopPropagation();
                if (!docId) return;
                setShowOtherOptions(false);
                onSendForSigning(String(docId), name);
            }}
        >
            Add to Magic Sign
        </div>
    );

    const reuploadItem = fileMetaData?.ingestionAction === INGESTION_ACTION.REUPLOAD && onReupload && (
        <div
            className={styles.DropdownItem}
            onClick={(e) => {
                e.stopPropagation();
                setShowOtherOptions(false);
                onReupload();
            }}
        >
            {REUPLOAD_MENU_LABEL}
        </div>
    );

    const chipTooltipContent = (chip: NonNullable<BankStatementFileMetaData['ingestionChip']>) => {
        if (!REASON_TOOLTIP_CHIPS.has(chip)) return CHIP_TOOLTIP_TEXT[chip];
        const details = fileMetaData?.ingestionDetails ?? [];
        if (!details.length) return fileMetaData?.ingestionReason ?? '';
        return (
            <>
                <div>{fileMetaData?.ingestionReason}</div>
                <ul className={styles.ChipTooltipDetails}>
                    {details.slice(0, MAX_STATUS_REASONS).map((detail, index) => (
                        <li key={`${index}-${detail}`}>{detail}</li>
                    ))}
                </ul>
            </>
        );
    };

    const dropdown = showOtherOptions
        ? createPortal(
              <div
                  ref={dropdownRef}
                  className={styles.OtherOptions}
                  style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
                  onClick={(e) => e.stopPropagation()}
              >
                  {isArchiveView ? (
                      <>
                          <MoveToActive
                              docId={docId}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleUnarchiveFileBulk={handleUnarchiveFileBulk}
                          />
                          <DeleteButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                              isArchive={false}
                          />
                          {magicSignItem}
                      </>
                  ) : (
                      <>
                          <MoveButton
                              docId={docId}
                              masterFolderName={masterFolderName}
                              setShowOtherOptions={setShowOtherOptions}
                              setShowDataRoomModal={setShowDataRoomModal}
                              user={user}
                              selectedCompany={selectedCompany}
                          />
                          {canAddToReports && (
                              <AddToReportsButton
                                  docId={docId}
                                  name={name}
                                  setShowOtherOptions={setShowOtherOptions}
                                  handleAddToReports={handleAddToReports}
                              />
                          )}
                          <DeleteButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                              isArchive={false}
                          />
                          {!isMcaRecordsView && (
                              <ArchiveButton
                                  docId={docId}
                                  name={name}
                                  masterFolderName={masterFolderName}
                                  fetchFiles={fetchFiles}
                                  setShowOtherOptions={setShowOtherOptions}
                                  user={user}
                                  selectedCompany={selectedCompany}
                                  handleDeleteFile={handleDeleteFile}
                              />
                          )}
                          {magicSignItem}
                          {reuploadItem}
                      </>
                  )}
              </div>,
              document.body,
          )
        : null;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (onItemSelect) {
            onItemSelect(fileId);
        }
    };

    // Signed/unsigned toggle rendered in the FileViewerDrawer header via renderHeaderExtra.
    // Only shown when the file has a signed copy AND the persona is allowed.
    const signedVersionToggle =
        showToggle && !isLenderOrPartner ? (
            <label className={styles.SignedCopyToggle} onClick={(e) => e.stopPropagation()}>
                <span className={styles.SignedCopyToggleLabel}>Show Signed Version</span>
                <span className={styles.Switch}>
                    <input
                        type="checkbox"
                        className={styles.SignedCopyToggleInput}
                        checked={showSignedVersion}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setShowSignedVersion(checked);
                            const targetId = checked ? signedDocId : docId;
                            if (targetId) handleFileClick(targetId, name);
                        }}
                        aria-label="Show Signed Version"
                    />
                    <span className={styles.SwitchSlider} />
                </span>
            </label>
        ) : undefined;

    return (
        <>
            {/* The whole row opens the file — checkbox / three-dots / dropdown / download
                all stop propagation so they don't also trigger the open handler. The
                drawers below are deliberately rendered OUTSIDE this div: they portal via
                BottomDrawer/SideDrawer, but stay in the same React tree, so a click inside
                an open drawer would otherwise bubble up to this onClick and re-open/refetch
                the file mid-edit. */}
            <div
                className={isLenderOrPartner ? styles.fileLenderView : styles.file}
                onClick={async () => {
                    if (!docId) return;
                    if (canEditSheet) await handleEditSheet(docId, name);
                    else await handleFileClick(activeDocId, name);
                }}
            >
                <div className={styles.nameCell}>
                    <div
                        style={{
                            // -1: the row now carries its own 1.25rem left padding (added to align
                            // Status/Data From/Data Till/Last Updated with the header), which already
                            // accounts for one indent level — this offsets so top-level (index=1) rows
                            // land exactly under the header checkbox instead of double-indenting.
                            marginLeft: `${(index - 1) * 1.25}rem`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        {showCheckboxes && (
                            <>
                                <span style={{ width: '0.75rem', display: 'inline-block' }}></span>
                                <div className={styles.checkboxCell} onClick={handleCheckboxClick}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={handleCheckboxChange}
                                        className={styles.checkbox}
                                    />
                                </div>
                            </>
                        )}
                        <span className={styles.FileIconContainer}>
                            <img src={fileIconDataRoom} alt={'folder'} />
                        </span>
                        <span className={styles.FileName} title={name} aria-label={name}>
                            {name}
                        </span>
                        {isNew && <span className={styles.NewBadge}>New</span>}
                    </div>
                </div>
                <span
                    className={
                        fileMetaData?.ingestionChip
                            ? `${styles.labelStatus} ${CHIP_TONE_CLASS[fileMetaData.ingestionChip] ?? ''}`.trim()
                            : ''
                    }
                >
                    {fileMetaData?.ingestionChip ? (
                        <Tooltip
                            content={chipTooltipContent(fileMetaData.ingestionChip)}
                            direction="top"
                            usePortal
                            fitContent
                            small
                            tipClassName={styles.ChipTooltipTip}
                            contentClassName={styles.ChipTooltipContent}
                        >
                            <span className={styles.IngestionChip}>
                                {fileMetaData.ingestionChip}
                                <img src={CHIP_INFO_ICON[fileMetaData.ingestionChip]} alt="" className={styles.ChipInfoIcon} />
                            </span>
                        </Tooltip>
                    ) : (
                        ''
                    )}
                </span>
                <span className={startDate && styles.DateContainer}>{getFormattedDate('file', startDate ?? '')}</span>
                <span className={endDate && styles.DateContainer}>{getFormattedDate('file', endDate ?? '')}</span>
                <span className={styles.FileDate}>{lastUpdated}</span>
                <span />
                {!isLenderOrPartner && dataRoomDownloadVisibility.show && (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!dataRoomDownloadVisibility.write) return;
                            if (activeDocId) downloadTheFile(activeDocId, name);

                            Mixpanel.track(AICA_DATAROOM_FILE_DOWNLOAD, {
                                userRole: user?.accountType,
                                pnoPlatform: getSubdomain(),
                                userId: user?.userId,
                                currentUrl: window.location.href,
                                organizationId: selectedCompany?.assesseeOrgId,
                                fileType: masterFolderName,
                            });
                        }}
                        className={styles.downloadIconContainer}
                        style={dataRoomDownloadVisibility.hidden ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        onMouseEnter={() => dataRoomDownloadVisibility.write && setDownloadImgUrl(downloadIconDataRoomHovered)}
                        onMouseLeave={() => dataRoomDownloadVisibility.write && setDownloadImgUrl(downloadIconDataRoomHovered)}
                    >
                        {downloadingFile ? (
                            <LoadingSpinner color="#6021B3" style={{ margin: '0', padding: '0' }} />
                        ) : (
                            <img src={downloadImgUrl} alt={'icon'} />
                        )}
                    </span>
                )}
                {/* {!isLenderOrPartner && <div className={styles.separator} />} */}
                {!isLenderOrPartner && (
                    <span
                        ref={threeDotsRef}
                        className={styles.ThreeDots}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowOtherOptions(!showOtherOptions);
                        }}
                    >
                        <img src={threeDotVertical} alt={'More'} />
                    </span>
                )}
                {dropdown}
            </div>
            {showFile && (
                <FileViewerDrawer
                    heading={name}
                    fileUrl={fileUrl}
                    mimeType={fileMimeType}
                    downloading={downloadingFile}
                    onDownload={() => {
                        if (activeDocId) downloadTheFile(activeDocId, name);
                    }}
                    onClose={() => {
                        setShowFile(false);
                        setShowSignedVersion(false);
                    }}
                    renderHeaderExtra={signedVersionToggle}
                />
            )}
            {showSheetEditor && (
                <SheetEditorDrawer
                    open={showSheetEditor}
                    fileUrl={fileUrl}
                    mimeType={fileMimeType}
                    fileName={name}
                    onClose={() => setShowSheetEditor(false)}
                    onAddToReports={onDrawerAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
            {showDocxDrawer && (
                <DocxDrawer
                    open={showDocxDrawer}
                    fileName={name}
                    fileUrl={fileUrl}
                    onClose={() => setShowDocxDrawer(false)}
                    onDownload={() => {
                        if (docId) downloadTheFile(docId, name);
                    }}
                    downloading={downloadingFile}
                    onAddToReports={onDrawerAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
        </>
    );
};

export default File;
